import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initSentry } from './lib/sentry';
import { initFacebookSdk } from './lib/facebookSdk';
import { registerPwa } from './lib/pwa';
import { reloadForStaleChunk } from './utils/lazyWithRetry';
import { hydrateStorage } from './lib/nativeStorage';
import { reloadAuthFromStorage } from './services/api/core';
import { initNativeApp, isNativePlatform } from './lib/nativeApp';
import { initNativeDeepLinks } from './lib/nativeAuth';
import { initWidgetDeepLinks } from './lib/nativeWidgets';
import { initPlatform } from './lib/platform';
import { OfflineBanner } from './components/OfflineBanner';
import './index.css';

/**
 * Build cache-buster: forces Vite to produce unique entry-chunk hashes per
 * deploy so that Vercel CDN never serves stale immutable-cached JS when the
 * lazy-loaded chunk graph changes between deploys.
 */
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__BUILD = '2026-05-27T1';

  // Vite fires `vite:preloadError` when a lazy chunk fails to preload — almost
  // always a stale chunk after a deploy. Reload once (guarded) to fetch the
  // fresh index.html instead of letting it surface as an uncaught error.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadForStaleChunk();
  });
}

// Initialize Sentry error tracking (if configured)
initSentry();

// Initialize Facebook SDK (if VITE_META_APP_ID is set)
initFacebookSdk();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * Boot sequence. On native we must hydrate persisted auth from secure storage
 * (async) and sync it into the API layer BEFORE the app renders, otherwise the
 * first render would see a logged-out state. On web this is a fast no-op.
 */
async function bootstrap() {
  // Tag <html> with host-OS classes (platform-macos / platform-apple /
  // platform-standalone) before anything renders so Apple-native CSS —
  // SF type, native scrollbars, gesture guards — applies from first paint.
  initPlatform();

  await hydrateStorage();
  reloadAuthFromStorage();

  // Web/PWA push uses the service worker; native uses the Capacitor FCM plugin,
  // so only register the SW off-device.
  if (!isNativePlatform()) {
    void registerPwa();
  }

  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('Root element #root not found');
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <OfflineBanner />
          <App />
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );

  // Native chrome (status bar, splash, back button) — after first paint.
  void initNativeApp();
  // OAuth deep-link handler (com.buzzbitx.app://auth/callback).
  void initNativeDeepLinks();
  // Widget-tap deep-link handler (com.buzzbitx.app://widget).
  void initWidgetDeepLinks();
}

void bootstrap();
